/**
 * AppProviders -- Provider Composition Wrapper
 *
 * Nesting order (outermost first):
 *   AppErrorBoundary > AuthCheckProvider > children
 *
 * AuthCheckProvider fetches /api/auth/me on mount and populates the Zustand
 * auth store. It renders children immediately (no blocking) so that the
 * route tree is always mounted. RouteGuard in App.tsx checks isAuthResolved
 * before enforcing role gating.
 *
 * On 401 during any subsequent API call, apiClient calls logout() directly,
 * clearing auth state and letting RouteGuard redirect to /login.
 */

import React, { useState, useEffect, useRef, type ReactNode } from "react";
import { AppErrorBoundary } from "../components/AppErrorBoundary";
import { useAuthStore } from "../stores/authStore";
import { useVersionStore } from "../stores/versionStore";
import { apiUrl } from "../config/api";
import type { User } from "../types";

// ---------------------------------------------------------------------------
// Auth Check -- calls /api/auth/me on mount, populates authStore.
// Resets auth store at mount to ensure test isolation (Zustand is a singleton).
// Renders children immediately; relies on RouteGuard to check isAuthResolved.
// ---------------------------------------------------------------------------

// Retry the initial /api/auth/me check on transient failures (5xx, network)
// before surfacing the error modal. In hosted environments a single cold
// backend or upstream blip on first paint shouldn't dead-end the user
// behind a modal that only a reload escapes. 401 is NOT retried — that
// means "not logged in" and the route guard handles redirect.
const AUTH_RETRY_DELAYS_MS = [250, 750];

function AuthCheckProvider({ children }: { children: ReactNode }) {
  const [authError, setAuthError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const setUser = useAuthStore((s) => s.setUser);
  const setAuthResolved = useAuthStore((s) => s.setAuthResolved);
  const setAuthLoading = useAuthStore((s) => s.setAuthLoading);

  // Reset all Zustand stores synchronously on first render for test isolation
  // (singletons persist between tests). Must happen before first paint so
  // RouteGuard and pages don't see stale state from previous tests.
  const mountedRef = useRef(false);
  if (!mountedRef.current) {
    mountedRef.current = true;
    useAuthStore.getState().reset();
    useVersionStore.getState().reset();
  }

  useEffect(() => {
    let cancelled = false;
    setAuthLoading(true);
    setIsRetrying(false);

    async function checkAuth() {
      const token = useAuthStore.getState().token;
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

      // Walk through [immediate, 250ms, 750ms]. Stop on a definitive answer
      // (200 OK or 401). Only retry on the gray-area outcomes (5xx, network).
      const attempts = [0, ...AUTH_RETRY_DELAYS_MS];
      for (let i = 0; i < attempts.length; i++) {
        if (cancelled) return;
        if (attempts[i] > 0) {
          await new Promise((r) => setTimeout(r, attempts[i]));
          if (cancelled) return;
        }
        try {
          const res = await fetch(apiUrl("/api/auth/me"), { headers });
          if (cancelled) return;
          if (res.ok) {
            const userData = (await res.json()) as User;
            if (!cancelled) {
              setUser(userData);
              setAuthError(false);
            }
            return;
          }
          if (res.status === 401) {
            if (!cancelled) {
              setUser(null);
              setAuthError(false);
            }
            return;
          }
          // 5xx or unexpected status — fall through to retry.
        } catch {
          // Network error — fall through to retry.
        }
      }
      // All attempts failed with transient errors.
      if (!cancelled) setAuthError(true);
    }

    checkAuth().finally(() => {
      if (!cancelled) {
        setAuthResolved(true);
        setAuthLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [setUser, setAuthResolved, setAuthLoading, retryNonce]);

  const handleRetry = () => {
    setIsRetrying(true);
    setAuthError(false);
    setRetryNonce((n) => n + 1);
  };

  return (
    <>
      {children}
      {authError && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50">
          <div role="alert" className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-600 mb-6">
              Unable to verify authentication. The server may be temporarily unavailable.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              disabled={isRetrying}
              className="inline-flex items-center justify-center px-4 py-2 bg-brand-crimson hover:bg-brand-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            >
              {isRetrying ? "Retrying..." : "Try again"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// AppProviders
// ---------------------------------------------------------------------------

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <AppErrorBoundary>
      <AuthCheckProvider>{children}</AuthCheckProvider>
    </AppErrorBoundary>
  );
}

export default AppProviders;
